import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { cn } from '~/lib/utils';
import { useEnforcedBudget, useRequestTopUp, type Budget } from '~/hooks/queries/use-budget';
import {
  BudgetBar,
  budgetTone,
  budgetToneText,
  describeReset,
  describeSource,
  formatBlocks,
  timeframeNoun,
} from './budget-meter';

/**
 * The person's own budget, in full (FRONT-1194).
 *
 * Renders nothing when the workspace has no cost control — the majority case,
 * and the platform's placeholder figures must never reach the screen.
 */
export function BudgetCard() {
  const budget = useEnforcedBudget();
  if (!budget) return null;

  const tone = budgetTone(budget);
  const reset = describeReset(budget.periodResetsAt);
  const source = describeSource(budget.source);

  return (
    <section className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-0.5">
          <Label className="text-xs text-muted-foreground">Compute budget</Label>
          <p className="text-[length:var(--text-2xs)] text-muted-foreground/80">
            What you can spend on EDDY {timeframeNoun(budget.timeframe)}. Set by your workspace.
          </p>
        </div>
        {budget.limitReached ? (
          <Badge variant="destructive-soft">Spent</Badge>
        ) : (
          <span className={cn('text-xs tabular-nums font-medium', budgetToneText(tone))}>
            {Math.round(budget.fractionUsed * 100)}% used
          </span>
        )}
      </div>

      <BudgetBar budget={budget} />

      <div className="flex items-baseline justify-between gap-3 text-[length:var(--text-2xs)] text-muted-foreground tabular-nums flex-wrap">
        <span>
          <span className="text-foreground font-medium">{formatBlocks(budget.used)}</span> of{' '}
          {formatBlocks(budget.limit)} used
          {budget.extensionCb > 0 && (
            <span className="text-muted-foreground/80">
              {' '}
              (includes {formatBlocks(budget.extensionCb)} topped up)
            </span>
          )}
        </span>
        {reset && <span>{reset}</span>}
      </div>

      {(source || budget.topUp.enabled) && (
        <div className="flex items-center justify-between gap-3 flex-wrap border-t border-border pt-3">
          <span className="text-[length:var(--text-2xs)] text-muted-foreground">{source}</span>
          {budget.topUp.enabled && <TopUpDialog budget={budget} />}
        </div>
      )}
    </section>
  );
}

/**
 * Self-service top-up request. The amounts and the project-code prompt are the
 * tenant's own configuration — we render what they set rather than inventing
 * options, and the platform enforces its own per-period request cap.
 */
export function TopUpDialog({
  budget,
  trigger,
}: {
  budget: Budget;
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<number | null>(budget.topUp.amounts[0] ?? null);
  const [projectCode, setProjectCode] = useState('');
  const request = useRequestTopUp();

  const submit = () => {
    if (!amount || !projectCode.trim()) return;
    request.mutate(
      { amount, projectCode: projectCode.trim() },
      {
        onSuccess: () => {
          toast.success('Top-up requested');
          setOpen(false);
          setProjectCode('');
        },
        onError: (err) => {
          // The platform refuses with a real reason once the per-period cap is
          // used up; show that rather than a generic failure.
          toast.error((err as Error).message || 'Could not request a top-up');
        },
      },
    );
  };

  return (
    <>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          Request more
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request more compute</DialogTitle>
            <DialogDescription>
              This asks your workspace for extra budget for the current period. It does not
              change your usual budget.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">How much</Label>
              <div className="flex flex-wrap gap-2">
                {budget.topUp.amounts.map((option) => (
                  <Button
                    key={option}
                    type="button"
                    size="sm"
                    variant={amount === option ? 'default' : 'outline'}
                    onClick={() => setAmount(option)}
                  >
                    {formatBlocks(option)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="budget-project-code" className="text-xs text-muted-foreground">
                Project code
              </Label>
              <Input
                id="budget-project-code"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                placeholder="e.g. FIN-2026"
              />
              <p className="text-[length:var(--text-2xs)] text-muted-foreground/80">
                {budget.topUp.codeGuidance ??
                  'Tells your workspace which project this extra usage belongs to.'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!amount || !projectCode.trim() || request.isPending}
            >
              {request.isPending ? 'Requesting…' : 'Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
